/**
 * KrokiMountFrame.jsx — kroki motorunun yerleşeceği REZERVE ALAN
 *
 * ⚠ BU ÇERÇEVE KASITLI OLARAK BOŞTUR. Kroki (salon taban planı) özelliği
 * geldiğinde çizim/gösterim buranın içine mount edilecek; şu an sadece yeri
 * tutuluyor, ölçüsü ve sayfadaki konumu hazır olsun diye.
 *
 * ── Kroki tarafını yazan geliştirici için ────────────────────────────────────
 * Her çerçeve DOM'a sabit bir tutamak bırakır:
 *
 *   <div id={mountId} data-kroki-mount={mount} data-fuar-id data-salon-id data-fuar-alani-id>
 *
 * Yani krokiyi bağlamak için sayfa dosyalarını didiklemene gerek yok:
 *   document.querySelector('[data-kroki-mount="salon-kroki"]')
 * ya da React tarafında bu bileşene `children` geçmen yeterli — children
 * verildiği anda placeholder içeriği kaybolur, çerçeve (ölçü + kenarlık) kalır.
 *
 * Kullanıldığı yerler:
 *   - pages/Management/Kroki/KrokiPage.jsx   → mount="fuar-salon-onizleme" (sekme 1)
 *                                             mount="kroki-cizim"          (sekme 2)
 *   - pages/Management/Kroki/SalonKrokiPage.jsx → mount="salon-kroki" (/fuar/salon/:salonId)
 */
import React from "react";
import { Badge } from "reactstrap";
import { Map as MapIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import i18n from "../../i18n";

const KrokiMountFrame = ({
  mount,                 // data-kroki-mount değeri — kroki tarafının seçici anahtarı
  mountId,               // opsiyonel sabit DOM id'si
  baslik = i18n.t("components:krokiMountFrame.krokiAlani"),
  aciklama,
  fuarId = null,
  salonId = null,
  fuarAlaniId = null,
  minHeight = 560,
  meta = [],             // [{ label, value }] — çerçevenin altında gösterilen bağlam künyesi
  children,              // doluysa placeholder yerine gerçek kroki içeriği render edilir
}) => {
  const { t } = useTranslation("components");
  const dolu = React.Children.count(children) > 0;

  return (
    <div
      id={mountId}
      data-kroki-mount={mount}
      data-fuar-id={fuarId ?? undefined}
      data-salon-id={salonId ?? undefined}
      data-fuar-alani-id={fuarAlaniId ?? undefined}
      className="rounded-3 bg-light d-flex flex-column align-items-center justify-content-center text-center p-4"
      style={{
        minHeight,
        // Rezerve alan olduğu görsel olarak da belli olsun diye kesikli kenarlık;
        // kroki geldiğinde bu bileşene children geçilerek içi doldurulacak.
        border: dolu ? "1px solid var(--vz-border-color)" : "2px dashed var(--vz-border-color)",
      }}
    >
      {dolu ? (
        children
      ) : (
        <>
          <div
            className="rounded-circle bg-white d-flex align-items-center justify-content-center mb-3 shadow-sm"
            style={{ width: 64, height: 64 }}
          >
            <MapIcon size={30} className="text-primary" />
          </div>

          <Badge color="warning" className="mb-3">{t("krokiMountFrame.yakinda")}</Badge>

          <h5 className="mb-2">{baslik}</h5>
          <p className="text-muted mb-3" style={{ maxWidth: 520 }}>
            {aciklama || t("krokiMountFrame.krokiOzelligiDevreyeAlindigindaSalon")}
          </p>

          {meta.length > 0 && (
            <div className="d-flex flex-wrap justify-content-center gap-3 mb-3">
              {meta.map((m) => (
                <div key={m.label} className="px-3 py-2 bg-white border rounded-3 text-start">
                  <div className="text-muted" style={{ fontSize: 11 }}>{m.label}</div>
                  <div className="fw-semibold">{m.value ?? "—"}</div>
                </div>
              ))}
            </div>
          )}

          {/* Kroki geliştiricisine bırakılan tutamak — sayfa kaynağını okumadan
              nereye bağlanacağını buradan görebilsin diye ekranda da yazıyor. */}
          <code className="text-muted small">
            {`[data-kroki-mount="${mount}"]`}
            {salonId != null ? ` · salonId=${salonId}` : ""}
            {fuarId != null ? ` · fuarId=${fuarId}` : ""}
            {fuarAlaniId != null ? ` · fuarAlaniId=${fuarAlaniId}` : ""}
          </code>
        </>
      )}
    </div>
  );
};

export default KrokiMountFrame;
